import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, ClickAwayListener, Fab, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { fetchAllowedSysTypeDocuments } from '../reduxStore/ScopeSlice';
import { TypeTiers } from '../constants/enums';

const CATEGORY_ORDER = [TypeTiers.CLIENT, TypeTiers.FOURNISSEUR, TypeTiers.AUCCUN, TypeTiers.EMPLOYE];
const CATEGORY_LABELS: Record<string, string> = {
  [TypeTiers.CLIENT]: 'Client',
  [TypeTiers.FOURNISSEUR]: 'Fournisseur',
  [TypeTiers.AUCCUN]: 'Interne',
  [TypeTiers.EMPLOYE]: 'Employé',
};

/**
 * Bouton flottant global (visible sur tout écran authentifié) pour créer un
 * document de vente de n'importe où dans l'app. Un clic ouvre un panneau
 * groupant les types de document par catégorie de tiers (Client / Fournisseur
 * / Interne), chaque catégorie affichée en ligne horizontale. Chaque type
 * navigue vers `/facturation/nouveau?type=CODE` — la route existante de création.
 *
 * Source des types alignée sur `SelectSysTypeDocument` (filtre de la liste) :
 * `ScopeSlice.allowedSysTypeDocuments` (liste noire par type de document,
 * dimension de scope distincte de DEPARTEMENT/DEPOT/CAISSE), pas la liste
 * brute `SysTypeDocumentSlice.sysTypeData` — sinon un type bloqué côté scope
 * apparaîtrait quand même ici. Filtré en plus par la permission RBAC
 * `documentVentes.{code}.create`.
 */
const NewDocumentSpeedDial: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const allowedSysTypeDocuments = useSelector((state: RootState) => state.ScopeSlice.allowedSysTypeDocuments);
  const permissions = useSelector((state: RootState) => state.AuthSlice.permissions) ?? [];

  useEffect(() => {
    if (allowedSysTypeDocuments.length === 0) dispatch(fetchAllowedSysTypeDocuments());
  }, [allowedSysTypeDocuments.length, dispatch]);

  const creatableTypes = useMemo(
    () => allowedSysTypeDocuments.filter((t) => permissions.includes(`documentVentes.${t.code}.create`)),
    [allowedSysTypeDocuments, permissions],
  );

  const groups = useMemo(
    () => CATEGORY_ORDER
      .map((cat) => ({
        key: cat,
        label: CATEGORY_LABELS[cat] ?? cat,
        items: creatableTypes.filter((t) => (t.type_tiers ?? TypeTiers.AUCCUN) === cat),
      }))
      .filter((g) => g.items.length > 0),
    [creatableTypes],
  );

  if (creatableTypes.length === 0) return null;

  const handlePick = (code: string) => {
    setOpen(false);
    navigate(`/facturation/nouveau?type=${code}`);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.speedDial }}>
        {open && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              bottom: 72,
              right: 0,
              p: 2,
              borderRadius: '16px',
              border: '1px solid #e4e7ec',
              maxWidth: { xs: 'calc(100vw - 48px)', sm: 480 },
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            {groups.map((g) => (
              <Box key={g.key} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.09em',
                    textTransform: 'uppercase', color: '#667085', mb: 0.75,
                  }}
                >
                  {g.label}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {g.items.map((t) => (
                    <Chip
                      key={t.code}
                      label={t.libelle}
                      onClick={() => handlePick(t.code)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: '#eef4ff',
                        color: '#2563eb',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#c7d2fe' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        <Fab
          color="primary"
          aria-label="Nouveau document"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <CloseIcon /> : <AddIcon />}
        </Fab>
      </Box>
    </ClickAwayListener>
  );
};

export default NewDocumentSpeedDial;
